import React from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';

import withModal from '../../../../components/global/modal/withModal';
import { CONTRIBUTOR_SHARE_MODAL } from '../../../../constants/modals';
import Spinner from '../../../../components/global/Spinner';
import {
  AlbumForContributorShare,
  AlbumFolderForContributorShare,
} from '../../../../graphql/queries/contributors';
import ShutterbugShare from './components/ShutterbugShare';
import { ALBUM_TYPE } from '../../../../constants/albums';
import ProShare from './components/ProShare/ProShare';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  album: PropTypes.shape({
    id: PropTypes.string,
    auto_contributor_share_url: PropTypes.string,
    album_folder: PropTypes.shape({
      id: PropTypes.string,
      auto_contributor_share_url: PropTypes.string,
    }),
  }),
  loading: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onRequestClose: PropTypes.func.isRequired,
};

const ContributorShare = ({
  album,
  albumFolder,
  loading,
  title,
  onRequestClose,
}) => {
  const entity = album ?? albumFolder;

  if (loading || !entity) {
    return (
      <div className="d-flex">
        <Spinner className="m-auto" />
      </div>
    );
  }

  const autoContributorLinkAvailable =
    entity.album_type === ALBUM_TYPE.SHUTTERBUG ||
    entity.album_type === ALBUM_TYPE.EVENT;

  return autoContributorLinkAvailable ? (
    <ShutterbugShare
      title={title}
      album={album}
      albumFolder={albumFolder}
      onRequestClose={onRequestClose}
    />
  ) : (
    <ProShare
      title={title}
      albumId={album?.id}
      albumFolderId={albumFolder?.id}
      onRequestClose={onRequestClose}
    />
  );
};

const withAlbumForContributorShare = graphql(AlbumForContributorShare, {
  options: ({ match, albumId }) => ({
    variables: {
      id: match.params.albumId || match.params.hierarchyItemId || albumId,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    album,
    albumFolder: album?.album_folder,
    loading,
  }),
  skip: ({ match, albumId }) =>
    !match.params.albumId && !match.params.hierarchyItemId && !albumId,
});

const withAlbumFolderForContributorShare = graphql(
  AlbumFolderForContributorShare,
  {
    options: ({ match, albumFolderId }) => ({
      variables: {
        id:
          match.params.folderId ||
          match.params.albumFolderId ||
          match.params.hierarchyItemId ||
          albumFolderId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loading,
    }),
    skip: ({ match, albumFolderId }) =>
      !match.params.folderId &&
      !match.params.albumFolderId &&
      !match.params.hierarchyItemId &&
      !albumFolderId,
  },
);

ContributorShare.propTypes = propTypes;

export { ContributorShare as ContributorShareComp };

const ContributorShareComposed = compose(
  withRouter,
  withAlbumForContributorShare,
  withAlbumFolderForContributorShare,
)(ContributorShare);

const ContributorShareModal = ({
  actionProps: { albumId, albumFolderId } = {},
  ...props
}) => {
  return (
    <ContributorShareComposed
      albumFolderId={albumFolderId}
      albumId={albumId}
      {...props}
    />
  );
};

export default withModal(ContributorShareModal, {
  name: CONTRIBUTOR_SHARE_MODAL,
  size: 'sm',
  type: 'full',
  hideHeader: true,
});
